import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

export interface ExpenseItem {
  id: string;
  dateOfExpense: string;
  item: string;
  rate: number;
  quantity: number;
  total?: number; // Optional explicit total - overrides calculated total
  comment: string;
}

interface ExpenseItemsTableProps {
  items: ExpenseItem[];
  onItemsChange: (items: ExpenseItem[]) => void;
  isExceptionRequest?: boolean;
}

const ExpenseItemsTable: React.FC<ExpenseItemsTableProps> = ({ items, onItemsChange, isExceptionRequest = false }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ExpenseItem>({
    id: '',
    dateOfExpense: new Date().toISOString().split('T')[0],
    item: '',
    rate: 0,
    quantity: 0,
    total: undefined,
    comment: '',
  });
  const [errors, setErrors] = useState<{
    dateOfExpense?: boolean;
    item?: boolean;
    rate?: boolean;
    quantity?: boolean;
  }>({});

  const generateId = () => `expense-${Date.now()}-${Math.random()}`;

  const handleOpenDialog = (item?: ExpenseItem) => {
    if (item) {
      setFormData(item);
      setEditingId(item.id);
    } else {
      setFormData({
        id: generateId(),
        dateOfExpense: new Date().toISOString().split('T')[0],
        item: '',
        rate: 0,
        quantity: 0,
        total: undefined,
        comment: '',
      });
      setEditingId(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setErrors({});
  };

  const handleFormChange = (field: keyof ExpenseItem, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveItem = () => {
    const newErrors: typeof errors = {};
    
    if (!formData.dateOfExpense) {
      newErrors.dateOfExpense = true;
    }
    if (!formData.item) {
      newErrors.item = true;
    }
    if (formData.rate <= 0) {
      newErrors.rate = true;
    }
    if (formData.quantity <= 0) {
      newErrors.quantity = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    if (editingId) {
      // Edit existing item
      const updatedItems = items.map((item) =>
        item.id === editingId ? formData : item
      );
      onItemsChange(updatedItems);
    } else {
      // Add new item
      onItemsChange([...items, formData]);
    }

    handleCloseDialog();
  };

  const handleDeleteItem = (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      onItemsChange(items.filter((item) => item.id !== id));
    }
  };

  const getItemTotal = (item: ExpenseItem) => item.total !== undefined ? item.total : item.rate * item.quantity;

  const calculateGrandTotal = items.reduce((sum, item) => sum + getItemTotal(item), 0).toFixed(2);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Expense Items
      </Typography>

      {/* Desktop Table View */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date of Expense</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Rate
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Quantity
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Total
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comment</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#999' }}>
                    No items added yet. Click "Add Item" to add expenses.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{item.dateOfExpense}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{item.item}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.875rem' }}>
                      ₹{item.rate.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.875rem' }}>
                      {item.quantity}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      ₹{getItemTotal(item).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.comment}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        sx={{ color: '#1976d2' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(item.id)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {items.length > 0 && (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: 600 }}>
                    Grand Total:
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1976d2' }}>
                        ₹{calculateGrandTotal}
                      </Typography>
                      {isExceptionRequest && calculateGrandTotal > 10000 && (
                        <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 500, display: 'block', mt: 0.5 }}>
                          ⚠️ Exceeds ₹10,000 limit
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {items.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', color: '#999', mb: 2 }}>
            No items added yet. Click "Add Item" to add expenses.
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {items.map((item) => (
              <Paper key={item.id} sx={{ p: 2, borderLeft: '4px solid #1976d2' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.dateOfExpense}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(item)}
                      sx={{ color: '#1976d2' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteItem(item.id)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Item
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.item}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Rate
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ₹{item.rate.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Quantity
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.quantity}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Total
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    ₹{getItemTotal(item).toFixed(2)}
                  </Typography>
                </Box>

                {item.comment && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Comment
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                      {item.comment}
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}

        {/* Mobile Grand Total */}
        {items.length > 0 && (
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', borderLeft: '4px solid #1976d2' }}>
            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
              Grand Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
              ₹{calculateGrandTotal}
            </Typography>
            {isExceptionRequest && calculateGrandTotal > 10000 && (
              <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 500, display: 'block', mt: 1 }}>
                ⚠️ Exceeds ₹10,000 limit
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      <Button
        startIcon={<AddIcon />}
        variant="contained"
        color="primary"
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        Add Item
      </Button>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Expense Item' : 'Add Expense Item'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Date of Expense"
              type="date"
              value={formData.dateOfExpense}
              onChange={(e) => handleFormChange('dateOfExpense', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              error={errors.dateOfExpense}
              helperText={errors.dateOfExpense ? 'Date is required' : ''}
            />
            <TextField
              fullWidth
              label="Item Name"
              value={formData.item}
              onChange={(e) => handleFormChange('item', e.target.value)}
              placeholder="e.g., Materials, Labor, Equipment"
              size="small"
              error={errors.item}
              helperText={errors.item ? 'Item name is required' : ''}
            />
            <TextField
              fullWidth
              label="Rate"
              type="number"
              value={formData.rate}
              onChange={(e) => handleFormChange('rate', parseFloat(e.target.value) || 0)}
              inputProps={{ step: '0.01', min: '0' }}
              size="small"
              error={errors.rate}
              helperText={errors.rate ? 'Rate must be greater than 0' : ''}
            />
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => handleFormChange('quantity', parseFloat(e.target.value) || 0)}
              inputProps={{ step: '0.01', min: '0' }}
              size="small"
              error={errors.quantity}
              helperText={errors.quantity ? 'Quantity must be greater than 0' : ''}
            />
            {formData.rate > 0 && formData.quantity > 0 && (
              <Box sx={{ p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
                <Typography variant="caption" sx={{ color: '#1565c0', display: 'block', mb: 0.5 }}>
                  Auto-calculated total: <strong>₹{(formData.rate * formData.quantity).toFixed(2)}</strong>
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  (Adjust below if needed)
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Total (Optional - leave blank to auto-calculate)"
              type="number"
              value={formData.total !== undefined ? formData.total : ''}
              onChange={(e) => handleFormChange('total', e.target.value ? parseFloat(e.target.value) : undefined)}
              inputProps={{ step: '0.01', min: '0' }}
              size="small"
              helperText="Enter a custom total if different from Rate × Quantity"
            />
            {formData.total !== undefined && formData.total > 0 && formData.rate * formData.quantity !== formData.total && (
              <Box sx={{ p: 1.5, backgroundColor: '#fff3e0', borderRadius: 1, borderLeft: '3px solid #f57c00' }}>
                <Typography variant="caption" sx={{ color: '#e65100', display: 'block', mb: 0.5 }}>
                  <strong>⚠ Total does not match Rate × Quantity</strong>
                </Typography>
                <Typography variant="caption" sx={{ color: '#d84315', fontSize: '0.75rem', lineHeight: 1.5 }}>
                  If this is intentional, consider adding a note in the comment field to help reviewers understand the cost difference. Otherwise, please verify the total.
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Comment (Optional)"
              value={formData.comment}
              onChange={(e) => handleFormChange('comment', e.target.value)}
              multiline
              rows={2}
              placeholder="Add any notes about this item"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveItem} variant="contained" color="primary">
            {editingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseItemsTable;
